/**
 * DeliveryOptions - Parent Web Component
 *
 * Orchestrates the delivery options feature including:
 * - Drawer open/close state
 * - Tab switching (pickup vs shipping)
 * - Row status indicators (collapsed card view)
 * - Coordination between child components
 * - Variant/Cart update propagation
 */

import { ThemeEvents } from '@theme/events';
import { Translator } from './translator.js';
import { morphSection } from '@theme/section-renderer';
import { CartUpdateEvent } from '@theme/events';

class DeliveryOptions extends HTMLElement {
  // Context types
  CONTEXT_PDP = 'pdp';
  CONTEXT_CART = 'cart';

  // Row status types
  STATUS_LOADING = 'loading';
  STATUS_NORMAL = 'normal';
  STATUS_AVAILABLE = 'available';
  STATUS_UNAVAILABLE = 'unavailable';

  // Configuration data from JSON
  data = {};

  // Translator instance
  translator = null;

  // Initialization flag
  initialized = false;

  // UI State
  state = {
    drawerOpen: false,
    activeTab: 'shipping',
    shipping: { status: 'normal', label: '' },
    pickup: { status: 'normal', label: '' }
  };

  // DOM References
  drawer = null;
  shippingComponent = null;
  pickupComponent = null;
  shippingRow = null;
  pickupRow = null;
  shippingTitle = null;
  pickupTitle = null;
  errorContainer = null;
  errorMessage = null;

  // Event handler references for cleanup
  variantUpdateHandler = null;
  cartUpdateHandler = null;

  constructor() {
    super();
  }

  connectedCallback() {
    if (this.initialized) {
      return;
    }
    this.initialized = true;

    this.initData();
    this.queryElements();

    if (!this.drawer) {
      console.error('DeliveryOptions: Drawer element not found.');
      return;
    }

    this.registerDrawerEvents();
    this.registerTabEvents();
    this.registerChildListeners();
    this.registerVariantUpdateListener();
    this.registerCartUpdateListener();

    // Initialize children with data
    this.initChildren();

    // Fetch variant inventory on PDP load
    if (!this.isCartContext() && this.data.variant_id) {
      this.fetchVariantInventory();
    }
  }

  disconnectedCallback() {
    this.removeEventListeners();
    this.initialized = false;
  }

  // ============================================
  // INITIALIZATION
  // ============================================

  /**
   * Parse configuration data from JSON script tag
   */
  initData() {
    const dataScript = this.querySelector('script[name="data"][type="application/json"]');

    if (!dataScript || !dataScript.textContent) {
      console.error('DeliveryOptions: Data script not found or empty.');
      this.data = { customer: {}, assets: {} };
      return;
    }

    try {
      this.data = JSON.parse(dataScript.textContent);
    } catch (error) {
      console.error('DeliveryOptions: Failed to parse data JSON.', error);
      this.data = { customer: {}, assets: {} };
    }

    // Initialize Translator (no namespace - access keys with full paths)
    this.translator = new Translator('delivery-translations');
  }

  /**
   * Query and store references to DOM elements
   */
  queryElements() {
    // Drawer
    this.drawer = this.querySelector('[ref="drawer"]');

    // Child components
    this.shippingComponent = this.querySelector('shipping-options');
    this.pickupComponent = this.querySelector('pickup-options');

    // Row elements
    this.shippingRow = this.querySelector('[ref="shipping-row"]');
    this.pickupRow = this.querySelector('[ref="pickup-row"]');
    this.shippingTitle = this.querySelector('[ref="shipping-title"]');
    this.pickupTitle = this.querySelector('[ref="pickup-title"]');

    // Status elements (single status element per row, updated dynamically)
    this.shippingStatus = this.querySelector('[ref="shipping-status"]');
    this.shippingStatusDot = this.querySelector('[ref="shipping-status-dot"]');
    this.shippingStatusText = this.querySelector('[ref="shipping-status-text"]');

    this.pickupStatus = this.querySelector('[ref="pickup-status"]');
    this.pickupStatusDot = this.querySelector('[ref="pickup-status-dot"]');
    this.pickupStatusText = this.querySelector('[ref="pickup-status-text"]');

    // In-store status elements (PDP only)
    this.pickupInstoreStatus = this.querySelector('[ref="pickup-instore-status"]');
    this.pickupInstoreStatusDot = this.querySelector('[ref="pickup-instore-status-dot"]');
    this.pickupInstoreStatusText = this.querySelector('[ref="pickup-instore-status-text"]');

    // Error container
    this.errorContainer = this.querySelector('[ref="error-container"]');
    this.errorMessage = this.querySelector('[ref="error-message"]');
  }

  /**
   * Initialize child components with data
   * Waits for custom elements to be defined before calling init
   */
  async initChildren() {
    // Wait for child custom elements to be defined
    await Promise.all([
      customElements.whenDefined('shipping-options'),
      customElements.whenDefined('pickup-options')
    ]);

    // Re-query in case elements were upgraded
    this.shippingComponent = this.querySelector('shipping-options');
    this.pickupComponent = this.querySelector('pickup-options');

    // Pass data to children via their init method
    if (this.shippingComponent && typeof this.shippingComponent.init === 'function') {
      this.shippingComponent.init(this.getShippingData());
    }

    if (this.pickupComponent && typeof this.pickupComponent.init === 'function') {
      this.pickupComponent.init(this.getPickupData());
    }
  }

  // ============================================
  // EVENT REGISTRATION
  // ============================================

  /**
   * Register drawer open/close events
   */
  registerDrawerEvents() {
    // Open drawer buttons
    this.querySelectorAll('[data-action="open-drawer"]').forEach(button => {
      button.addEventListener('click', (e) => {
        const tab = e.currentTarget.dataset.tab || 'shipping';
        this.openDrawer(tab);
      });
    });

    // Close drawer button
    this.querySelector('[data-action="close-drawer"]')?.addEventListener('click', () => {
      this.closeDrawer();
    });

    // Close on backdrop click
    this.drawer.addEventListener('click', (e) => {
      if (e.target === this.drawer) {
        this.closeDrawer();
      }
    });

    // Close on Escape key
    this.drawer.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.closeDrawer();
      }
    });

    // Handle animation end for closing
    this.drawer.addEventListener('animationend', () => {
      if (this.drawer.classList.contains('dialog-closing')) {
        this.drawer.classList.remove('dialog-closing');
        this.drawer.close();
        this.state.drawerOpen = false;

        // Notify children that drawer has closed
        // This allows them to restore confirmed state
        this.pickupComponent?.handleDrawerClose?.();
        this.shippingComponent?.handleDrawerClose?.();
      }
    });
  }

  /**
   * Register tab switching events
   */
  registerTabEvents() {
    this.querySelectorAll('.delivery-options__tab').forEach(tab => {
      tab.addEventListener('click', () => {
        const tabName = tab.dataset.tab;
        this.switchTab(tabName);
      });
    });
  }

  /**
   * Register listeners for child component events
   */
  registerChildListeners() {
    // Status change events
    this.shippingComponent?.addEventListener('status-change', (e) => {
      this.handleShippingStatusChange(e.detail);
    });

    this.pickupComponent?.addEventListener('status-change', (e) => {
      this.handlePickupStatusChange(e.detail);
    });

    // Store selected event - close drawer when user confirms a store
    this.pickupComponent?.addEventListener('store-selected', (e) => {
      this.handleStoreSelected(e.detail);
    });
  }

  /**
   * Register listener for variant update events (PDP context only)
   */
  registerVariantUpdateListener() {
    if (this.isCartContext()) {
      return;
    }

    const closestSection = this.closest('.shopify-section, dialog');
    if (closestSection) {
      this.variantUpdateHandler = this.handleVariantUpdate.bind(this);
      closestSection.addEventListener(ThemeEvents.variantUpdate, this.variantUpdateHandler);
    }
  }

  /**
   * Register listener for cart update events (Cart context only)
   */
  registerCartUpdateListener() {
    if (!this.isCartContext()) {
      return;
    }

    this.cartUpdateHandler = this.handleCartUpdate.bind(this);
    document.addEventListener(ThemeEvents.cartUpdate, this.cartUpdateHandler);
  }

  /**
   * Remove all event listeners
   */
  removeEventListeners() {
    // Remove variant update listener
    if (this.variantUpdateHandler) {
      const closestSection = this.closest('.shopify-section, dialog');
      if (closestSection) {
        closestSection.removeEventListener(ThemeEvents.variantUpdate, this.variantUpdateHandler);
      }
    }

    // Remove cart update listener
    if (this.cartUpdateHandler) {
      document.removeEventListener(ThemeEvents.cartUpdate, this.cartUpdateHandler);
    }
  }

  // ============================================
  // DRAWER MANAGEMENT
  // ============================================

  /**
   * Open the drawer and switch to specified tab
   * @param {string} tab - 'pickup' or 'shipping'
   */
  openDrawer(tab = 'shipping') {
    this.switchTab(tab);
    this.drawer.showModal();
    this.state.drawerOpen = true;
    document.body.style.overflow = 'hidden';
    this.hideError();
  }

  /**
   * Close the drawer with animation
   */
  closeDrawer() {
    this.drawer.classList.add('dialog-closing');
    document.body.style.overflow = '';
  }

  /**
   * Switch active tab
   * @param {string} tabName - 'pickup' or 'shipping'
   */
  switchTab(tabName) {
    this.state.activeTab = tabName;

    // Update tab buttons
    this.querySelectorAll('.delivery-options__tab').forEach(tab => {
      tab.setAttribute('aria-selected', tab.dataset.tab === tabName ? 'true' : 'false');
    });

    // Update panels
    this.querySelectorAll('.delivery-options__panel').forEach(panel => {
      const panelRef = panel.getAttribute('ref');
      const isActive = panelRef === `panel-${tabName}`;
      panel.hidden = !isActive;
    });
  }

  // ============================================
  // ROW STATUS MANAGEMENT
  // ============================================

  /**
   * Handle shipping component status change
   * @param {Object} detail - Event detail with status, label, error, statusText
   */
  handleShippingStatusChange({ status, label = '', error = null, statusText = '' }) {
    this.state.shipping = { status, label, statusText };
    this.updateRowStatus('shipping', status, label, statusText);

    if (error) {
      this.showError(error);
    } else {
      this.hideError();
    }
  }

  /**
   * Handle pickup component status change
   * @param {Object} detail - Event detail with status, label, error, statusText, instoreStatus, instoreText
   */
  handlePickupStatusChange({ status, label = '', error = null, statusText = '', instoreStatus = '', instoreText = '' }) {
    this.state.pickup = { status, label, statusText, instoreStatus, instoreText };
    this.updateRowStatus('pickup', status, label, statusText);

    // Update in-store status (PDP only)
    this.updateInstoreStatus(instoreStatus, instoreText);

    if (error) {
      this.showError(error);
    } else {
      this.hideError();
    }
  }

  /**
   * Handle store selected event from pickup component
   * @param {Object} detail - Event detail with store, status, statusText, instoreStatus, instoreText
   */
  async handleStoreSelected({ store, status, statusText = '', instoreStatus = '', instoreText = '' }) {
    // Update the row status with the selected store name and availability text
    this.state.pickup = { status, label: store?.name || '', statusText, instoreStatus, instoreText };
    this.updateRowStatus('pickup', status, store?.name || '', statusText);

    // Update in-store status (PDP only)
    this.updateInstoreStatus(instoreStatus, instoreText);

    // Remove gift wrap message/From/To fields from cart items
    await this.#removeGiftWrapProperties();

    // Close the drawer
    this.closeDrawer();
  }

  /**
   * Remove gift wrap properties (Message, From, To) from all cart items with gift wrap
   */
  async #removeGiftWrapProperties() {
    try {
      // Fetch current cart
      const cartResponse = await fetch('/cart.js');
      if (!cartResponse.ok) {
        console.error('Failed to fetch cart');
        return;
      }

      const cart = await cartResponse.json();
      if (!cart.items || cart.items.length === 0) {
        return;
      }

      // Find all gift wrap items
      const giftWrapItems = cart.items.filter((item) => {
        // Check if item has gift wrap properties (To, From, Message)
        const hasGiftWrapProperties = item.properties && (
          item.properties.To ||
          item.properties.From ||
          item.properties.Message
        );
        return hasGiftWrapProperties;
      });

      if (giftWrapItems.length === 0) {
        return;
      }

      // Get section IDs for cart updates
      const sectionIds = this.#getSectionIds();
      if (sectionIds.length === 0) {
        console.error('No section IDs found for cart update');
        return;
      }

      // Update each gift wrap item sequentially to ensure proper order
      let lastResponse = null;
      for (const item of giftWrapItems) {
        // Get all properties except To, From, Message (set them to empty string to remove)
        const filteredProperties = {};
        if (item.properties) {
          Object.keys(item.properties).forEach((key) => {
            if (key !== 'To' && key !== 'From' && key !== 'Message') {
              filteredProperties[key] = item.properties[key];
            } else {
              filteredProperties[key] = '';
            }
          });
        }

        // Update the cart item with sections included
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: item.key,
            quantity: item.quantity,
            properties: filteredProperties,
            sections: sectionIds.join(','),
            sections_url: window.location.pathname,
          }),
        });

        if (!response.ok) {
          console.error(`Failed to update gift wrap item ${item.key}`);
          continue;
        }

        lastResponse = await response.json();
      }

      // Update UI with sections from the last response
      if (lastResponse && lastResponse.sections) {
        // Morph all sections that were updated
        sectionIds.forEach((sectionId) => {
          if (lastResponse.sections[sectionId]) {
            morphSection(sectionId, lastResponse.sections[sectionId]);
          }
        });

        // Dispatch cart update event to refresh UI
        document.dispatchEvent(
          new CartUpdateEvent({}, sectionIds[0] || 'cart', {
            itemCount: lastResponse.item_count,
            source: 'delivery-options-gift-wrap-cleanup',
            sections: lastResponse.sections,
          })
        );
      }
    } catch (error) {
      console.error('Error removing gift wrap properties:', error);
    }
  }

  /**
   * Get section IDs for cart updates
   * @returns {string[]}
   */
  #getSectionIds() {
    /** @type {string[]} */
    const ids = [];
    document.querySelectorAll('cart-items-component').forEach((el) => {
      const id = /** @type {HTMLElement} */ (el).dataset.sectionId;
      if (id) ids.push(id);
    });
    return ids;
  }

  /**
   * Update in-store status display (PDP only)
   * @param {string} status - In-store status ('available', 'partial', 'unavailable', or empty)
   * @param {string} text - In-store status text
   */
  updateInstoreStatus(status, text) {
    // Only show in-store status on PDP context
    if (this.isCartContext()) {
      if (this.pickupInstoreStatus) {
        this.pickupInstoreStatus.classList.add('hidden');
      }
      return;
    }

    // Hide if no status provided (e.g., loading, normal states)
    if (!status || status === 'loading' || status === 'normal') {
      if (this.pickupInstoreStatus) {
        this.pickupInstoreStatus.classList.add('hidden');
      }
      return;
    }

    // Show in-store status
    if (this.pickupInstoreStatus) {
      this.pickupInstoreStatus.classList.remove('hidden');
      this.pickupInstoreStatus.dataset.status = status;
    }

    // Update dot color
    if (this.pickupInstoreStatusDot) {
      this.pickupInstoreStatusDot.classList.remove(
        'delivery-options__status-dot--green',
        'delivery-options__status-dot--orange',
        'delivery-options__status-dot--red'
      );

      switch (status) {
        case 'available':
          this.pickupInstoreStatusDot.classList.add('delivery-options__status-dot--green');
          break;
        case 'partial':
          this.pickupInstoreStatusDot.classList.add('delivery-options__status-dot--orange');
          break;
        case 'unavailable':
          this.pickupInstoreStatusDot.classList.add('delivery-options__status-dot--red');
          break;
      }
    }

    // Update text
    if (this.pickupInstoreStatusText && text) {
      this.pickupInstoreStatusText.textContent = text;
    }
  }

  /**
   * Update a row's visual status
   * Uses single status element pattern (like popup) - updates data-status, dot color, and text
   *
   * @param {'shipping' | 'pickup'} type - Row type
   * @param {string} status - Status type ('loading', 'normal', 'available', 'partial', 'unavailable')
   * @param {string} label - Label text (e.g., postcode or store name)
   * @param {string} statusText - Status text to display (e.g., "5 of 10 items available")
   */
  updateRowStatus(type, status, label = '', statusText = '') {
    const row = type === 'shipping' ? this.shippingRow : this.pickupRow;
    const title = type === 'shipping' ? this.shippingTitle : this.pickupTitle;
    const statusElement = type === 'shipping' ? this.shippingStatus : this.pickupStatus;
    const statusDot = type === 'shipping' ? this.shippingStatusDot : this.pickupStatusDot;
    const statusTextElement = type === 'shipping' ? this.shippingStatusText : this.pickupStatusText;

    if (!row) return;

    // Update data-state attribute on row
    row.dataset.state = status;

    // Disable row while loading
    row.disabled = status === this.STATUS_LOADING;

    // Update title if label provided
    if (title) {
      const baseTitle = type === 'shipping'
        ? this.t('delivery.delivery_label')
        : this.t('delivery.pickup_label');

      // Show location label for available, partial, or unavailable status
      const showLocationLabel = status === this.STATUS_AVAILABLE ||
                                status === 'partial' ||
                                status === this.STATUS_UNAVAILABLE;

      if (label && showLocationLabel) {
        title.textContent = type === 'shipping'
          ? this.t('delivery.delivery_to', { location: label })
          : this.t('delivery.pickup_from', { location: label });
      } else {
        title.textContent = baseTitle;
      }
    }

    // Update status element's data-status attribute
    if (statusElement) {
      statusElement.dataset.status = status;
    }

    // Update dot color based on status
    if (statusDot) {
      // Remove all color classes
      statusDot.classList.remove(
        'delivery-options__status-dot--green',
        'delivery-options__status-dot--orange',
        'delivery-options__status-dot--red'
      );

      // Check if this is a partial availability case (for shipping)
      // Partial availability is indicated by statusText containing "of" and "items available"
      const isPartialAvailability = status === 'available' && 
        type === 'shipping' && 
        statusText && 
        /of.*items available/i.test(statusText);

      // Add appropriate color class based on status
      switch (status) {
        case 'available':
          // Use orange dot for partial availability, green for full availability
          if (isPartialAvailability) {
            statusDot.classList.add('delivery-options__status-dot--orange');
          } else {
            statusDot.classList.add('delivery-options__status-dot--green');
          }
          break;
        case 'partial':
          statusDot.classList.add('delivery-options__status-dot--orange');
          break;
        case 'unavailable':
          statusDot.classList.add('delivery-options__status-dot--red');
          break;
        // 'loading' and 'normal' don't show a colored dot
      }
    }

    // Update status text
    if (statusTextElement) {
      // Use provided statusText, or fallback to default messages
      if (statusText) {
        statusTextElement.textContent = statusText;
      } else {
        // Default messages based on status
        switch (status) {
          case 'loading':
            statusTextElement.textContent = this.t('accessibility.loading', {}, 'Loading...');
            break;
          case 'normal':
            statusTextElement.textContent = type === 'shipping'
              ? this.t('delivery.shipping.check_availability', {}, 'Check availability')
              : this.t('delivery.pickup.check_availability', {}, 'Select store for availability');
            break;
          case 'available':
            statusTextElement.textContent = this.t('delivery.available');
            break;
          case 'partial':
            statusTextElement.textContent = this.t('delivery.partially_available', {}, 'Partially available');
            break;
          case 'unavailable':
            statusTextElement.textContent = this.t('delivery.not_available');
            break;
        }
      }
    }
  }

  // ============================================
  // VARIANT/CART UPDATE HANDLERS
  // ============================================

  /**
   * Handle variant update events (PDP context)
   * @param {CustomEvent} event - Variant update event
   */
  handleVariantUpdate(event) {
    // Check if this is for our product
    const eventProductId = event.detail?.data?.productId;
    const localProductId = this.dataset?.productId;

    if (eventProductId && localProductId && eventProductId !== localProductId) {
      return;
    }

    const variant = event.detail.resource;
    if (!variant) {
      return;
    }

    // Update internal data
    this.data.variant_id = variant.id;
    this.data.available = variant.available ?? false;
    this.data.requires_shipping = variant.requires_shipping ?? false;

    // Fetch inventory for new variant
    if (variant.id) {
      this.fetchVariantInventory();
    }

    // Notify children via method calls
    const variantData = {
      variantId: variant.id,
      available: variant.available,
      requiresShipping: variant.requires_shipping
    };

    if (this.shippingComponent && typeof this.shippingComponent.handleVariantChange === 'function') {
      this.shippingComponent.handleVariantChange(variantData);
    }

    if (this.pickupComponent && typeof this.pickupComponent.handleVariantChange === 'function') {
      this.pickupComponent.handleVariantChange(variantData);
    }
  }

  /**
   * Handle cart update events (Cart context)
   * @param {CustomEvent} event - Cart update event
   */
  handleCartUpdate(event) {
    // Wait for DOM to update
    requestAnimationFrame(() => {
      // Re-read data from updated script tag
      this.initData();

      // Re-query elements (they may have been replaced)
      this.queryElements();

      // Re-register child listeners
      this.registerChildListeners();

      // Notify children with properly formatted data
      if (this.shippingComponent && typeof this.shippingComponent.handleCartChange === 'function') {
        this.shippingComponent.handleCartChange(this.getShippingData());
      }

      if (this.pickupComponent && typeof this.pickupComponent.handleCartChange === 'function') {
        this.pickupComponent.handleCartChange(this.getPickupData());
      }
    });
  }

  // ============================================
  // ERROR HANDLING
  // ============================================

  /**
   * Show error message in drawer
   * @param {string} message - Error message to display
   */
  showError(message) {
    if (this.errorContainer && this.errorMessage) {
      this.errorMessage.textContent = message;
      this.errorContainer.hidden = false;
    }
  }

  /**
   * Hide error message
   */
  hideError() {
    if (this.errorContainer) {
      this.errorContainer.hidden = true;
    }
  }

  // ============================================
  // VARIANT INVENTORY API
  // ============================================

  /** Key used by pickup-options for confirmed store in localStorage */
  static PICKUP_CONFIRMED_STORE_KEY = 'amb.pickup_confirmed_store';

  /**
   * Whether user has already selected/confirmed a pickup store.
   * Checks both component state and localStorage (localStorage covers PDP load before pickup init).
   * @returns {boolean}
   */
  hasPickupStoreSelected() {
    if (this.pickupComponent?.state?.confirmedStore) {
      return true;
    }
    try {
      const raw = localStorage.getItem(DeliveryOptions.PICKUP_CONFIRMED_STORE_KEY);
      if (!raw) return false;
      const parsed = JSON.parse(raw);
      return !!(parsed && (parsed.id || parsed.name));
    } catch {
      return false;
    }
  }

  /**
   * Fetch variant inventory by location and update status text
   * Only called on PDP context
   */
  async fetchVariantInventory() {
    if (this.isCartContext()) {
      return;
    }

    const variantId = this.data.variant_id;
    if (!variantId) {
      return;
    }

    // Skip updating pickup row when user already has a selected store
    const hasPickupStoreSelected = this.hasPickupStoreSelected();

    // Show loading state for both shipping and pickup (skip pickup if store already selected)
    if (this.shippingStatusText) {
      this.updateRowStatus('shipping', this.STATUS_LOADING, '', this.t('accessibility.loading', {}, 'Loading...'));
    }
    if (this.pickupStatusText && !hasPickupStoreSelected) {
      this.updateRowStatus('pickup', this.STATUS_LOADING, '', this.t('accessibility.loading', {}, 'Loading...'));
      this.updateInstoreStatus('loading', '');
    }

    // Get location ID from data (set from shop metafield based on localization)
    const locationId = this.data.location_id;
    if (!locationId) {
      return;
    }

    try {
      const response = await fetch('/apps/api/get-variants-inventory-by-location', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          variantIds: [variantId],
          locationId: locationId,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.error) {
        throw new Error(data.error);
      }

      // Find the variant in the response
      const variantData = data.variants?.find((v) => {
        // Handle both numeric and string IDs, and GID format
        const responseId = v.id?.toString().replace('gid://shopify/ProductVariant/', '');
        const currentId = variantId.toString();
        return responseId === currentId || v.id?.toString() === currentId;
      });

      // Find add-to-cart component in the same section
      const section = this.closest('.shopify-section');
      const addToCartComponent = section?.querySelector('add-to-cart-component');

      if (variantData) {
        const isAvailable = variantData.available || false;
        
        // Update status based on availability
        const status = isAvailable ? this.STATUS_AVAILABLE : this.STATUS_UNAVAILABLE;
        const statusText = isAvailable
          ? this.t('delivery.available')
          : this.t('delivery.not_available');

        // Update shipping status
        this.updateRowStatus('shipping', status, '', statusText);
        
        // Update pickup only when no store is selected (keep selected store label/status)
        if (!hasPickupStoreSelected) {
          const line1 = isAvailable
            ? this.t('delivery.pickup.cc_available', {}, 'Click and collect available')
            : this.t('delivery.pickup.line_item_unavailable', {}, 'Click and collect not available');
          const line2 = isAvailable
            ? this.t('delivery.pickup.instore_available', {}, 'In-store')
            : this.t('delivery.pickup.check_availability', {}, 'Select store for availability');
          this.updateRowStatus('pickup', status, '', line1);
          this.updateInstoreStatus(status, line2);
          this.pickupInstoreStatusDot.classList.add('delivery-options__status-dot--green');
        }

        // Disable/enable add to cart button based on availability
        if (addToCartComponent && typeof addToCartComponent.disable === 'function' && typeof addToCartComponent.enable === 'function') {
          const textEl = addToCartComponent.querySelector('.add-to-cart-text__content');
          if (isAvailable) {
            addToCartComponent.enable();
            if (textEl) {
              textEl.textContent = addToCartComponent._atcOriginalText !== undefined
                ? addToCartComponent._atcOriginalText
                : this.t('products.product.add_to_cart', {}, 'Add To Bag');
              addToCartComponent._atcOriginalText = undefined;
            }
          } else {
            if (textEl) {
              if (addToCartComponent._atcOriginalText === undefined) {
                addToCartComponent._atcOriginalText = textEl.textContent;
              }
              textEl.textContent = this.t('products.product.sold_out', {}, 'Out of Stock');
            }
            addToCartComponent.disable();
          }
        }
      } else {
        // Variant not found in response, use default status
        this.updateRowStatus('shipping', this.STATUS_NORMAL, '', this.t('delivery.shipping.check_availability', {}, 'Check availability'));
        if (!hasPickupStoreSelected) {
          this.updateRowStatus('pickup', this.STATUS_NORMAL, '', this.t('delivery.pickup.check_availability', {}, 'Select store for availability'));
          this.updateInstoreStatus('normal', '');
        }
        
        // Enable add to cart button if variant not found (fallback to default behavior)
        if (addToCartComponent && typeof addToCartComponent.enable === 'function') {
          addToCartComponent.enable();
          const textEl = addToCartComponent.querySelector('.add-to-cart-text__content');
          if (textEl) {
            textEl.textContent = addToCartComponent._atcOriginalText !== undefined
              ? addToCartComponent._atcOriginalText
              : this.t('products.product.add_to_cart', {}, 'Add To Bag');
            addToCartComponent._atcOriginalText = undefined;
          }
        }
      }
    } catch (error) {
      console.error('DeliveryOptions: Failed to fetch variant inventory:', error);
      // Reset to normal state on error (skip pickup if user already has a selected store)
      const hasPickupStoreSelectedInCatch = this.hasPickupStoreSelected();
      this.updateRowStatus('shipping', this.STATUS_NORMAL, '', this.t('delivery.shipping.check_availability', {}, 'Check availability'));
      if (!hasPickupStoreSelectedInCatch) {
        this.updateRowStatus('pickup', this.STATUS_NORMAL, '', this.t('delivery.pickup.check_availability', {}, 'Select store for availability'));
        this.updateInstoreStatus('normal', '');
      }
      
      // Enable add to cart button on error (fallback to default behavior)
      const section = this.closest('.shopify-section');
      const addToCartComponent = section?.querySelector('add-to-cart-component');
      if (addToCartComponent && typeof addToCartComponent.enable === 'function') {
        addToCartComponent.enable();
        const textEl = addToCartComponent.querySelector('.add-to-cart-text__content');
        if (textEl) {
          textEl.textContent = addToCartComponent._atcOriginalText !== undefined
            ? addToCartComponent._atcOriginalText
            : this.t('products.product.add_to_cart', {}, 'Add To Bag');
          addToCartComponent._atcOriginalText = undefined;
        }
      }
    }
  }

  // ============================================
  // HELPERS
  // ============================================

  /**
   * Check if this is cart context
   * @returns {boolean}
   */
  isCartContext() {
    return this.data.context === this.CONTEXT_CART;
  }

  /**
   * Get shipping-specific data for child component
   * @returns {Object}
   */
  getShippingData() {
    return {
      context: this.data.context,
      variantId: this.data.variant_id,
      available: this.data.available,
      requiresShipping: this.data.requires_shipping,
      lineItems: this.data.line_items || [],
      quantities: this.data.quantities || [],
      defaultCountry: this.data.default_country,
      countryIsoCode: this.data.country_iso_code,
      currency: this.data.currency,
      currencySymbol: this.data.currency_symbol,
      sectionId: this.data.section_id,
      customer: this.data.customer,
      assets: this.data.assets,
      auspostPriceData: this.data.auspost_price_data,
      availableCountries: this.data.available_countries,
      translations: this.data.translations,
      locationId: this.data.location_id // Location ID from shop metafield based on localization
    };
  }

  /**
   * Get pickup-specific data for child component
   * @returns {Object}
   */
  getPickupData() {
    return {
      context: this.data.context,
      variantId: this.data.variant_id,
      available: this.data.available,
      lineItems: this.data.line_items || [],
      shippableLineItems: this.data.shippable_line_items || [],
      digitalLineItems: this.data.digital_line_items || [],
      subscriptionLineItems: this.data.subscription_line_items || [],
      shippableLineItemsKey: this.data.shippable_line_items_key || [],
      quantities: this.data.quantities || [],
      sectionId: this.data.section_id,
      storefront: this.data.storefront,
      assets: this.data.assets,
      translations: this.data.translations,
      cc_safety_stock: this.data.cc_safety_stock,
      instore_safety_stock: this.data.instore_safety_stock,
      countryIsoCode: this.data.country_iso_code,
      defaultCountry: this.data.default_country,
      googleMapsApiKey: this.data.googleMapsApiKey,
      location_id: this.data.location_id ?? null
    };
  }

  /**
   * Get translation for a key using Translator service
   * @param {string} key - Translation key (relative to 'delivery' namespace)
   * @param {Object} variables - Optional variables for interpolation
   * @returns {string}
   */
  t(key, variables = {}) {
    if (this.translator) {
      return this.translator.translate(key, variables);
    }
    return key;
  }
}

// Define the custom element
if (!customElements.get('delivery-options')) {
  customElements.define('delivery-options', DeliveryOptions);
}

export { DeliveryOptions };
